import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CLEAR_WEATHER,
  CONDITION_TINT,
  fetchLocation,
  fetchWeather,
  hourInTimeZone,
  isWeatherId,
} from './ambient/weather';
import {
  TOD_WINDOWS,
  applyWeatherTint,
  interpolatePalette,
  resolveTOD,
} from './ambient/palettes';
import { PLACE_STORAGE_KEY, placeById, toPlaceOverride } from './places';

const DesignContext = createContext(null);

const SF_FALLBACK = {
  city: 'San Francisco',
  country: 'United States',
  latitude: 37.7749,
  longitude: -122.4194,
  timeZone: 'America/Los_Angeles',
};


const MODE_KEY = 'ada-mode-pref';
const AMBIENT_KEY = 'ada-ambient';
const LEGACY_SKIN_KEY = 'ada-design-skin';

export const MODE_PREFS = ['system', 'light', 'dark'];

/** Legacy skin ids → mode pref (skin concept retired for one light/dark pair). */
const LEGACY_SKIN_TO_MODE = {
  auto: 'system',
  glass: 'dark',
  paper: 'light',
  circuit: 'dark',
  pulse: 'system',
  atelier: 'system',
  gold: 'dark',
  holo: 'dark',
  quantum: 'dark',
  sunlight: 'light',
};

function readPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readModeOverride() {
  if (typeof window === 'undefined') return null;
  const m = new URLSearchParams(window.location.search).get('mode');
  return m === 'light' || m === 'dark' ? m : null;
}

function readTODOverride() {
  if (typeof window === 'undefined') return null;
  const t = new URLSearchParams(window.location.search).get('tod');
  return TOD_WINDOWS.some((w) => w.id === t) ? t : null;
}

function readWeatherOverride() {
  if (typeof window === 'undefined') return null;
  const w = new URLSearchParams(window.location.search).get('wx');
  return isWeatherId(w) ? w : null;
}

/**
 * Mode pref: explicit user choice wins; then legacy keys migrate once;
 * default follows the device (`system`).
 */
function readInitialModePref() {
  const override = readModeOverride();
  if (override) return override;
  if (typeof window === 'undefined') return 'system';
  const saved = window.localStorage.getItem(MODE_KEY);
  if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  const legacySkin = window.localStorage.getItem(LEGACY_SKIN_KEY);
  if (legacySkin && LEGACY_SKIN_TO_MODE[legacySkin]) {
    return LEGACY_SKIN_TO_MODE[legacySkin];
  }
  const legacyDark = window.localStorage.getItem('darkMode');
  if (legacyDark != null && window.localStorage.getItem(MODE_KEY) == null) {
    return JSON.parse(legacyDark) ? 'dark' : 'light';
  }
  return 'system';
}

function readInitialAmbient() {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(AMBIENT_KEY) !== 'off';
}

function msUntilNextMinuteBoundary() {
  const now = Date.now();
  return 60_000 - (now % 60_000) + 50;
}

function resolveMode(modePref, prefersDark) {
  if (modePref === 'light' || modePref === 'dark') return modePref;
  return prefersDark ? 'dark' : 'light';
}

export function DesignProvider({ children }) {
  const [modePref, setModePrefState] = useState(readInitialModePref);
  const [ambient, setAmbientState] = useState(readInitialAmbient);
  const [prefersDark, setPrefersDark] = useState(readPrefersDark);
  const [weather, setWeather] = useState(CLEAR_WEATHER);
  const [location, setLocation] = useState(null);
  const [placeOverride, setPlaceOverride] = useState(() => {
    try { const saved = localStorage.getItem(PLACE_STORAGE_KEY); const place = saved ? placeById(saved) : null; return place ? toPlaceOverride(place) : null; } catch { return null; }
  });
  const [clockTick, setClockTick] = useState(0);

  const modeOverride = useRef(readModeOverride()).current;
  const todOverride = useRef(readTODOverride()).current;
  const wxOverride = useRef(readWeatherOverride()).current;

  const resolvedMode = modeOverride || resolveMode(modePref, prefersDark);

  const setModePref = useCallback((pref) => {
    if (!MODE_PREFS.includes(pref)) return;
    setModePrefState(pref);
    try {
      window.localStorage.setItem(MODE_KEY, pref);
    } catch {
      /* storage disabled — pref lives for this session only */
    }
  }, []);

  const setAmbient = useCallback((on) => {
    setAmbientState(on);
    try {
      window.localStorage.setItem(AMBIENT_KEY, on ? 'on' : 'off');
    } catch {
      /* best-effort persistence */
    }
  }, []);

  // Cycle system → dark → light → system (chip button on login + shell).
  const cycleModePref = useCallback(() => {
    setModePref(
      modePref === 'system' ? 'dark' : modePref === 'dark' ? 'light' : 'system'
    );
  }, [modePref, setModePref]);

  // Time-of-day: static midday when ambient is off, else live clock.
  const hourFloat = useMemo(
    () => hourInTimeZone(placeOverride?.timeZone || location?.timeZone),
    [clockTick, ambient, placeOverride, location?.timeZone] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const tod = useMemo(
    () => (todOverride || !ambient
      ? { id: todOverride || 'midday', label: '', progress: 0.5 }
      : resolveTOD(hourFloat)),
    [todOverride, ambient, hourFloat]
  );

  const condition = wxOverride || (ambient ? weather.condition : 'clear');
  const tint = ambient ? CONDITION_TINT[condition] || 1 : 1;

  // Final CSS variable set for the current minute.
  const vars = useMemo(() => {
    const base = interpolatePalette(tod.id, resolvedMode, tod.progress);
    return applyWeatherTint(base, tint, resolvedMode);
  }, [tod, resolvedMode, tint]);

  // Apply vars + mode/tod/wx attributes to <html>.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-mode', resolvedMode);
    root.setAttribute('data-tod', todOverride || (ambient ? tod.id : 'midday'));
    root.setAttribute('data-wx', condition);
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [vars, resolvedMode, tod, todOverride, ambient, condition]);

  // Keep mobile browser chrome in sync with the ambient background.
  useEffect(() => {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', vars['--ada-bg']);
  }, [vars]);

  // Follow the device while pref is `system`.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setPrefersDark(e.matches);
    setPrefersDark(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const weatherTick = Math.floor(clockTick / 15);

  // Location + weather (best-effort; never blocks paint).
  // User-picked city wins; else IP; else San Francisco.
  useEffect(() => {
    if (!ambient || typeof window === 'undefined') return undefined;
    const controller = new AbortController();
    (async () => {
      const loc =
        placeOverride ||
        (await fetchLocation(controller.signal)) || {
          ...SF_FALLBACK,
          fetchedAt: Date.now(),
        };
      if (controller.signal.aborted) return;
      setLocation(loc);
      const wx = await fetchWeather(loc.latitude, loc.longitude, controller.signal);
      if (wx && !controller.signal.aborted) setWeather({ ...CLEAR_WEATHER, ...wx });
    })();
    return () => controller.abort();
  }, [ambient, placeOverride, weatherTick]);

  // Minute tick so the palette drifts without a reload.
  useEffect(() => {
    let timeoutId;
    let intervalId;
    const bump = () => setClockTick((n) => n + 1);
    timeoutId = window.setTimeout(() => {
      bump();
      intervalId = window.setInterval(bump, 60_000);
    }, msUntilNextMinuteBoundary());
    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, []);

  // Hotkeys: Alt+1 light, Alt+2 dark, Alt+3 system (Alt+0 = toggle ambient).
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey) return;
      if (e.target?.closest?.('input, textarea, [contenteditable=true]')) return;
      if (e.key === '1') setModePref('light');
      else if (e.key === '2') setModePref('dark');
      else if (e.key === '3') setModePref('system');
      else if (e.key === '0') setAmbient(!ambient);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setModePref, setAmbient, ambient]);

  const value = useMemo(
    () => ({
      modePref,
      resolvedMode,
      setModePref,
      cycleModePref,
      ambient,
      setAmbient,
      todWindow: tod.id,
      todLabel: tod.label,
      todProgress: tod.progress,
      condition,
      weather,
      location,
      setPlaceOverride,
      vars,
      prefersDark,
    }),
    [
      modePref,
      resolvedMode,
      setModePref,
      cycleModePref,
      ambient,
      setAmbient,
      tod,
      condition,
      weather,
      location,
      setPlaceOverride,
      vars,
      prefersDark,
    ]
  );

  return (
    <DesignContext.Provider value={value}>{children}</DesignContext.Provider>
  );
}

export function useDesign() {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error('useDesign must be used within DesignProvider');
  return ctx;
}
